import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { fetchDashboardData } from '@/api/dashboard'
import { 
  Award, 
  Ticket, 
  Target, 
  BookOpen,
  ArrowRight,
  Bell
} from 'lucide-react'
import { cn, getLevelColor, formatDate } from '@/lib/utils'

export function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboardData,
  })
  console.log('Dashboard:', { isLoading, hasData: !!data, error: error?.message })

  if (isLoading) {
    return <DashboardSkeleton />
  }

  const { stats, announcements, nextMilestones, partner } = data || {}

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="rounded-lg bg-gradient-to-r from-cloudflare-navy to-cloudflare-navy-light p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome back, {partner?.name?.split(' ')[0] || 'Partner'}!</h1>
        <p className="mt-1 text-gray-300">
          Here's your partner services dashboard for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Certifications"
          value={`${stats?.certificationsCompleted || 0}/${stats?.totalCertifications || 0}`}
          description="Requirements completed"
          icon={Award}
          href="/certifications"
        />
        <StatCard
          title="Support Tickets"
          value={stats?.openTickets || 0}
          description="Open tickets"
          icon={Ticket}
          href="/support"
        />
        <StatCard
          title="Upcoming Milestones"
          value={stats?.upcomingMilestones || 0}
          description="Certification goals"
          icon={Target}
          href="/certifications"
        />
        <StatCard
          title="Enablement"
          value={stats?.enablementCompleted || 0}
          description="Resources completed"
          icon={BookOpen}
          href="/enablement"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Next Milestones */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-cloudflare-orange" />
              Next Certification Milestones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {nextMilestones?.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <Award className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">
                  All certifications completed! You're a Cloudflare expert.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {nextMilestones?.slice(0, 3).map((milestone: any) => (
                  <div key={milestone.id} className="flex items-center gap-4">
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full',
                      getStatusColor(milestone.status)
                    )}>
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{milestone.title}</h3>
                        <Badge 
                          variant="secondary" 
                          className={cn('text-white', getLevelColor(milestone.level))}
                        >
                          {milestone.level}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{milestone.description}</p>
                    </div>
                    <Badge variant={milestone.status === 'in_progress' ? 'default' : 'outline'}>
                      {milestone.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-cloudflare-orange" />
              Announcements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {announcements?.slice(0, 5).map((announcement: any) => (
                <div key={announcement.id} className="border-b border-border pb-3 last:border-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{announcement.title}</h4>
                    {announcement.priority === 'high' && (
                      <Badge variant="destructive" className="text-xs">Important</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                    {announcement.body}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatDate(announcement.published_at)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StatCard({ 
  title, 
  value, 
  description, 
  icon: Icon,
  href 
}: { 
  title: string
  value: string | number
  description: string
  icon: any
  href: string
}) {
  return (
    <Card className="card-hover cursor-pointer" onClick={() => window.location.href = href}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="mt-2 text-3xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cloudflare-orange/10">
            <Icon className="h-6 w-6 text-cloudflare-orange" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-24 rounded-lg bg-muted animate-pulse" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="h-20 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: 'bg-green-500',
    in_progress: 'bg-yellow-500',
    not_started: 'bg-gray-400'
  }
  return colors[status] || 'bg-gray-400'
}
